import type React from "react";
import { useMemo } from "react";

import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Button,
  Alert,
} from "reactstrap";

import Loader from "@/components/Loader";
import { useGetCampaignLandingsQuery } from "app/apiSlice";
import ClipboardWriteButton from "components/ClipboardWriteButton";

interface LandingPageLinkModalProps {
  isOpen: boolean;
  onClose?: () => void;
  campaignId: string;
}

export const LandingPageLinkModal: React.FC<LandingPageLinkModalProps> = ({
  isOpen,
  onClose,
  campaignId,
}: LandingPageLinkModalProps) => {
  // Get landing page data for this campaign
  const { data: existingLandings, isLoading } = useGetCampaignLandingsQuery(
    { campaign_id: campaignId },
    { skip: !campaignId || !isOpen },
  );

  // Get the first landing page for this campaign
  const existingLanding = useMemo(() => {
    return Array.isArray(existingLandings)
      ? existingLandings.find(
        (landing: any) => landing.campaign_id === campaignId,
      )
      : null;
  }, [existingLandings, campaignId]);

  const landingPageUrl = useMemo(() => {
    return `${import.meta.env.VITE_CREATORS_URL}/join/${existingLanding?._id}`;
  }, [existingLanding?._id]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        Landing Page Link and Instructions
      </ModalHeader>
      <ModalBody>
        {isLoading ? (
          <Loader />
        ) : (
          <div className="d-flex flex-column gap-3">
            <p className="mb-3">
              Share this link with creators - they will be able to view the
              campaign landing page and join the campaign:
            </p>
            {/* TODO #campaignLanding: add button to switch to landing page editor */}
            {!existingLanding ? (
              <Alert color="warning" className="mt-2">
                <strong>Note:</strong> No landing page content found for this
                campaign. Please edit the landing page content first using the
                &quot;Edit Landing Page&quot; button on the campaign.
              </Alert>
            ) : (
              <>
                {/* TODO #campaignLanding: refactor clipboard write as I need to display custom element for the display text */}
                <ClipboardWriteButton textToCopy={landingPageUrl} />
              </>
            )}
            <Alert color="info" className="mt-3">
              <p className="mb-3">
                <strong>
                  Anyone with this link can view the campaign landing page
                </strong>{" "}
                and join the campaign.
              </p>
              <p className="mb-3">
                You can share campaign landing pages with creators{" "}
                <strong>
                  regardless of whether they already have an account or not
                </strong>
                . If a creator joins the campaign using this link they will be
                asked to sign in or create an account.
              </p>
              <p className="mb-0">
                Anyone joining a campaign from a landing page will be{" "}
                <strong>
                  assigned to the campaign with a default rate of $0.
                </strong>
              </p>
            </Alert>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};
